// src/pages/MyLessons.js
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';
import {
  TrashIcon,
  PlusIcon,
  PencilIcon,
  FolderIcon,
  FolderOpenIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/solid';

export default function MyLessons() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [dragOverFolder, setDragOverFolder] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      setError('');
      try {
        const [{ data: lessonData, error: lessonErr }, { data: folderData, error: folderErr }] =
          await Promise.all([
            supabase
              .from('lessons')
              .select('id, title, level, folder_id, created_at')
              .eq('owner_id', user.id)
              .order('created_at', { ascending: false }),
            supabase
              .from('lesson_folders')
              .select('id, name, created_at')
              .eq('owner_id', user.id)
              .order('name', { ascending: true }),
          ]);

        if (lessonErr) throw lessonErr;
        if (folderErr) throw folderErr;

        setLessons(lessonData || []);
        setFolders(folderData || []);

        // Init collapsed by default
        const collapsed = {};
        (folderData || []).forEach((f) => (collapsed[f.id] = false));
        setExpandedFolders(collapsed);
      } catch (err) {
        console.error(err);
        setError('Failed to load lessons and folders.');
      }
    };

    fetchData();
  }, [user, navigate]);

  const grouped = useMemo(() => {
    const map = {};
    folders.forEach((f) => (map[f.id] = []));
    lessons.forEach((l) => {
      if (l.folder_id && map[l.folder_id]) map[l.folder_id].push(l);
    });
    return map;
  }, [folders, lessons]);

  const filteredUnassigned = useMemo(() => {
    return lessons
      .filter((l) => !l.folder_id)
      .filter((l) => l.title.toLowerCase().includes(search.toLowerCase()));
  }, [lessons, search]);

  const filteredInFolder = (folderId) =>
    (grouped[folderId] || []).filter((l) =>
      l.title.toLowerCase().includes(search.toLowerCase())
    );

  const handleToggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson permanently?')) return;
    const { error } = await supabase.from('lessons').delete().eq('id', lessonId);
    if (error) {
      alert('Failed to delete lesson: ' + error.message);
      return;
    }
    setLessons((prev) => prev.filter((l) => l.id !== lessonId));
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const { data, error } = await supabase
        .from('lesson_folders')
        .insert([{ name: newFolderName.trim(), owner_id: user.id }])
        .select()
        .single();

      if (error) throw error;

      setFolders((prev) => [...prev, data]);
      setExpandedFolders((prev) => ({ ...prev, [data.id]: true }));
      setNewFolderName('');
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to create folder.');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Lessons will remain but move to Unassigned.')) return;
    const { error } = await supabase.from('lesson_folders').delete().eq('id', folderId);
    if (error) {
      alert('Failed to delete folder: ' + error.message);
      return;
    }
    // Local updates
    setFolders((prev) => prev.filter((f) => f.id !== folderId));
    setLessons((prev) => prev.map((l) => (l.folder_id === folderId ? { ...l, folder_id: null } : l)));
  };

  const startRenameFolder = (folderId, currentName) => {
    setRenamingFolderId(folderId);
    setRenameValue(currentName);
  };

  const saveRenameFolder = async () => {
    if (!renamingFolderId || !renameValue.trim()) {
      setRenamingFolderId(null);
      setRenameValue('');
      return;
    }
    const id = renamingFolderId;
    try {
      const { error } = await supabase
        .from('lesson_folders')
        .update({ name: renameValue.trim() })
        .eq('id', id);
      if (error) throw error;
      setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, name: renameValue.trim() } : f)));
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to rename folder.');
    } finally {
      setRenamingFolderId(null);
      setRenameValue('');
    }
  };

  // Drag + drop
  const onDragStart = (e, lessonId) => e.dataTransfer.setData('lesson-id', lessonId);
  const onDragOver = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(folderId);
  };
  const onDragLeave = () => setDragOverFolder(null);

  const onDropToFolder = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    const lessonId = e.dataTransfer.getData('lesson-id');
    const { error } = await supabase.from('lessons').update({ folder_id: folderId }).eq('id', lessonId);
    if (error) {
      alert('Failed to move lesson.');
      return;
    }
    setLessons((prev) => prev.map((l) => (l.id === lessonId ? { ...l, folder_id: folderId } : l)));
  };

  const onDropToUnassigned = async (e) => {
    e.preventDefault();
    const lessonId = e.dataTransfer.getData('lesson-id');
    const { error } = await supabase.from('lessons').update({ folder_id: null }).eq('id', lessonId);
    if (error) {
      alert('Failed to move lesson.');
      return;
    }
    setLessons((prev) => prev.map((l) => (l.id === lessonId ? { ...l, folder_id: null } : l)));
  };

  const totalLessons = lessons.length;
  const totalFolders = folders.length;

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4 md:p-8">
      {/* Header / Toolbar */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">My Lessons</h2>
          <p className="text-sm text-gray-600">
            {totalLessons} lesson{totalLessons !== 1 ? 's' : ''} • {totalFolders} folder{totalFolders !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search lessons…"
              className="w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <Link to="/modules/create">
            <button className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-500">
              <PlusIcon className="h-5 w-5" />
              Create Lesson
            </button>
          </Link>
          <button
            onClick={() => setEditMode((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-5 w-5" />
            {editMode ? 'Done' : 'Edit'}
          </button>
        </div>
      </div>

      {/* Create Folder */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="New folder name"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={handleCreateFolder}
            disabled={creatingFolder}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-white hover:bg-green-500 disabled:opacity-60"
          >
            <PlusIcon className="h-5 w-5" />
            {creatingFolder ? 'Creating…' : 'Create'}
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {/* Folders */}
      {folders.length > 0 ? (
        <div className="space-y-5">
          {folders.map((folder) => {
            const isOpen = expandedFolders[folder.id];
            const folderLessons = filteredInFolder(folder.id);
            const isDragTarget = dragOverFolder === folder.id;

            return (
              <section
                key={folder.id}
                className={`rounded-xl border bg-white ${isDragTarget ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'}`}
                onDragOver={(e) => onDragOver(e, folder.id)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDropToFolder(e, folder.id)}
              >
                <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                  <button
                    onClick={() => handleToggleFolder(folder.id)}
                    className="inline-flex items-center gap-2 text-left"
                  >
                    {isOpen ? (
                      <FolderOpenIcon className="h-5 w-5 text-yellow-500" />
                    ) : (
                      <FolderIcon className="h-5 w-5 text-yellow-500" />
                    )}
                    <span className="font-semibold text-gray-900">
                      {renamingFolderId === folder.id ? (
                        <input
                          className="rounded border px-2 py-1 text-sm"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRenameFolder();
                            if (e.key === 'Escape') {
                              setRenamingFolderId(null);
                              setRenameValue('');
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        folder.name
                      )}
                    </span>
                    <span className="text-sm text-gray-500">• {folderLessons.length} item{folderLessons.length !== 1 ? 's' : ''}</span>
                  </button>

                  {editMode && (
                    <div className="inline-flex items-center gap-2">
                      {renamingFolderId === folder.id ? (
                        <button
                          onClick={saveRenameFolder}
                          className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          Save
                        </button>
                      ) : (
                        <button
                          onClick={() => startRenameFolder(folder.id, folder.name)}
                          className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          Rename
                        </button>
                      )}
                      <button onClick={() => handleDeleteFolder(folder.id)} className="p-1 hover:bg-red-50 rounded">
                        <TrashIcon className="h-5 w-5 text-red-500" />
                      </button>
                      <EllipsisVerticalIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </header>

                {isOpen && (
                  <div className="grid grid-cols-1 gap-3 p-4">
                    {folderLessons.length === 0 ? (
                      <p className="text-sm text-gray-500">No lessons in this folder.</p>
                    ) : (
                      folderLessons.map((lesson) => (
                        <LessonRow
                          key={lesson.id}
                          lesson={lesson}
                          onDragStart={onDragStart}
                          onDelete={() => handleDeleteLesson(lesson.id)}
                          editMode={editMode}
                        />
                      ))
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <EmptyState />
      )}

      {/* Unassigned */}
      <section
        className="rounded-xl border border-dashed border-gray-300 bg-white p-4"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropToUnassigned}
      >
        <header className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Unassigned</h3>
          <span className="text-sm text-gray-500">{filteredUnassigned.length} item{filteredUnassigned.length !== 1 ? 's' : ''}</span>
        </header>
        <div className="grid grid-cols-1 gap-3">
          {filteredUnassigned.length === 0 ? (
            <p className="text-sm text-gray-500">No unassigned lessons.</p>
          ) : (
            filteredUnassigned.map((lesson) => (
              <LessonRow
                key={lesson.id}
                lesson={lesson}
                onDragStart={onDragStart}
                onDelete={() => handleDeleteLesson(lesson.id)}
                editMode={editMode}
              />
            ))
          )}
        </div>
      </section>

      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );
}

function LessonRow({ lesson, onDragStart, onDelete, editMode }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lesson.id)}
      className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 hover:shadow-sm"
    >
      <div className="min-w-0">
        <Link
          to={`/modules/${lesson.level}/${lesson.id}`}
          className="truncate font-medium text-blue-700 hover:underline"
          title={lesson.title}
        >
          {lesson.title}
        </Link>
        <p className="text-xs text-gray-500">
          Level: <span className="font-medium">{lesson.level || '—'}</span>
        </p>
      </div>
      {editMode && (
        <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded">
          <TrashIcon className="h-5 w-5 text-red-500" />
        </button>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
      <p className="text-gray-700">
        No folders yet. Create your first folder to organize lessons.
      </p>
    </div>
  );
}
